import { isOONCaseUser } from 'src/components/User/utils';
import PropTypes from 'prop-types';
import React from 'react';
import {
  SimpleSelectField,
} from '@unite-us/app-components';
import featureFlag from 'src/common/utils/FeatureFlag/FeatureFlag';
import {
  propagateSelectBlur,
  propagateSelectChange,
  resolveFieldError,
  resolveSelectedValue,
} from './selectFieldHelpers';
import {
  buildProgramNextValue,
  getCaseTypeType,
  getFilteredProgrammingOptions,
  toProgramSelectOptions,
} from './caseProgramFieldHelpers';

export const CaseProgramField = ({
  values,
  groupId,
  isCoordinationGroup,
  programmingOptionsArray,
  user,
  input,
  meta,
  onChange,
  ...props
}) => {
  const userCanReferOutOfNetwork = isCoordinationGroup || isOONCaseUser(user, groupId);
  const caseTypeType = getCaseTypeType(values);
  const filteredProgrammingOptionsArray = getFilteredProgrammingOptions({
    programmingOptionsArray,
    userCanReferOutOfNetwork,
    caseTypeType,
  });

  const options = React.useMemo(
    () => toProgramSelectOptions(filteredProgrammingOptionsArray),
    [filteredProgrammingOptionsArray],
  );

  const value = React.useMemo(
    () => resolveSelectedValue(input?.value),
    [input?.value],
  );

  const inputRef = React.useRef(input);
  React.useEffect(() => {
    inputRef.current = input;
  }, [input]);

  const handleChange = React.useCallback((selectedId) => {
    const nextValue = buildProgramNextValue(selectedId, programmingOptionsArray);
    propagateSelectChange({ inputRef, onChange, value: nextValue });
  }, [programmingOptionsArray, onChange]);

  const handleBlur = React.useCallback(
    () => propagateSelectBlur(inputRef),
    [],
  );

  return (
    <SimpleSelectField
      options={options}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      required
      searchable
      clearable={false}
      error={resolveFieldError(meta)}
      {...props}
    />
  );
};

CaseProgramField.propTypes = {
  disabled: PropTypes.bool,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object,
  values: PropTypes.object.isRequired,
  groupId: PropTypes.string.isRequired,
  id: PropTypes.string,
  isCoordinationGroup: PropTypes.bool.isRequired,
  label: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  programmingOptionsArray: PropTypes.array.isRequired,
  required: PropTypes.bool,
  user: PropTypes.shape({
    networks: PropTypes.array.isRequired,
  }).isRequired,
};

CaseProgramField.defaultProps = {
  id: 'program',
  label: 'Program',
  placeholder: 'Choose one...',
  required: true,
  disabled: false,
  meta: undefined,
  onChange: undefined,
};

export default featureFlag(CaseProgramField);
